// The body of the Plans & documents sheet, opened from the pod in hand.
//
// Two sections, from two places, answering to two different things:
//
//   · DRAWINGS belong to the pod. They are the controlled drawings in
//     SharePoint, in the folder Odoo binds to the pod's type (or, failing
//     that, its project), listed by Odoo with each sheet's revision and
//     approval status read fresh from the library. See api.drawings and
//     server/src/http/drawings.ts.
//
//   · STANDARD WORK belongs to the station. The ISW library links each card
//     to the Odoo work centres it is used at; this shows the cards for the
//     work centre the app is set to, and drills through to every card in that
//     work centre's department. See lib/podDocs.ts.
//
// A drawing opens in the device's own PDF viewer. An engineering sheet is an
// A1 page read by pinching in on one corner of it, and the platform's viewer
// already does that well; a viewer of our own would be a PDF renderer shipped
// to every phone to do the same job worse. A card opens here, over the pod, in
// the library's own viewer.

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DrawingVM, IswRegisterRow, PodVM } from '@/api/types';
import { Banner } from '@/components/ui/Banner';
import { Icon } from '@/components/ui/Icon';
import { Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { drawingTone, iswsForDepartment, iswsForStation, leafName } from '@/lib/podDocs';
import { useDrawings, useIswRegister, useWorkcentres } from '@/queries';
import { CardViewer, LANG_FOR_UI, StatusPill } from '@/screens/phone/IswLibrary';
import { useStore } from '@/store';

export function PodDocs({ pod, wcName }: { pod: PodVM | null | undefined; wcName: string | null }) {
  const t = useT();
  const iswAvailable = useStore((s) => s.iswAvailable);

  if (!pod) {
    return <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)' }}>{t.noDocuments}</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <p style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)', margin: 0 }}>
        {[pod.mo, pod.proj, wcName].filter(Boolean).join(' · ')}
      </p>
      {/* Keyed on the pod so a folder drilled into on one pod is not the
          folder the next pod opens on. */}
      <Drawings key={pod.moId} moId={pod.moId} />
      {/* Not offered at all on a deployment with no library behind it, as the
          More tab does not offer the library row. */}
      {iswAvailable && <StandardWork wcName={wcName} />}
    </div>
  );
}

// ── drawings ─────────────────────────────────────────────────────────────────

function Drawings({ moId }: { moId: number }) {
  const t = useT();
  // Where the operator has drilled to, as a stack: null is the pod's own
  // folder. Back pops one, and the name it shows is the folder it returns to.
  const [trail, setTrail] = useState<Array<{ id: string | null; name: string }>>([]);
  const [folderId, setFolderId] = useState<string | null>(null);
  const q = useDrawings(moId, folderId);
  const data = q.data;

  const enter = (id: string) => {
    setTrail((s) => [...s, { id: folderId, name: data?.folder?.name ?? '' }]);
    setFolderId(id);
  };
  const back = () => {
    const prev = trail[trail.length - 1];
    if (!prev) return;
    setTrail((s) => s.slice(0, -1));
    setFolderId(prev.id);
  };

  const unapproved = (data?.drawings ?? []).some((d) => drawingTone(d) !== 'ok');
  const prev = trail[trail.length - 1];

  return (
    <section aria-label={t.drawingsT}>
      <SectionHead label={t.drawingsT} note={data?.folder?.name ?? null} />

      {prev && <BackButton label={`${t.backToT} ${prev.name || t.drawingsT}`} onClick={back} />}

      {q.isPending ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={50} radius={12} />
          <Skeleton height={50} radius={12} />
          <Skeleton height={50} radius={12} />
        </div>
      ) : q.isError ? (
        <Banner tone="danger" action={{ label: t.retry, onClick: () => void q.refetch() }}>
          {t.drawingsFailed} — {(q.error as Error).message}
        </Banner>
      ) : !data?.folder ? (
        // No folder bound to the pod type or the project. Odoo's own words,
        // because they say who fixes it and where.
        <Muted>{data?.unavailable || t.noDocuments}</Muted>
      ) : (
        <>
          {data.folders.map((f) => (
            <Row
              key={f.id}
              icon="folder"
              tone="accent"
              title={f.name}
              meta={`${f.count} ${f.count === 1 ? t.drawingsItem : t.drawingsItems}`}
              onClick={() => enter(f.id)}
            />
          ))}
          {data.drawings.map((d) => (
            <DrawingRow key={d.id} d={d} />
          ))}
          {data.folders.length === 0 && data.drawings.length === 0 && <Muted>{t.drawingsNone}</Muted>}
          {/* The project's folder beside a pod-type folder: shared sheets live
              there, and the floor should not have to guess that. */}
          {data.siblings.map((s) => (
            <Row key={s.id} icon="folder" tone="sub" title={s.name} onClick={() => enter(s.id)} />
          ))}

          {unapproved && (
            <p
              style={{
                font: '600 10.5px/1.45 Manrope,sans-serif',
                color: 'var(--danger)',
                background: 'var(--surface2)',
                borderRadius: 10,
                padding: '9px 11px',
                margin: '10px 0 0',
              }}
            >
              {t.drawingsUnapproved}
            </p>
          )}
          {data.drawings.length > 0 && (
            <p style={{ font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', margin: '8px 2px 0' }}>
              {t.drawingsViewer}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function DrawingRow({ d }: { d: DrawingVM }) {
  const t = useT();
  const tone = drawingTone(d);
  const meta = [d.revision ? `${t.drawingRev} ${d.revision}` : null, fmtSize(d.size)].filter(Boolean).join(' · ');
  return (
    <Row
      icon="file"
      tone="danger"
      title={d.name}
      meta={meta}
      trailing="external"
      disabled={!d.url}
      chip={
        tone === 'ok'
          ? undefined
          : { label: d.status || t.drawingNoStatus, background: tone === 'danger' ? 'var(--danger)' : 'var(--warn)' }
      }
      // Its own tab, not this one: the drawing replaces nothing in the app,
      // and Back from the viewer should land on the pod exactly as it was.
      onClick={() => d.url && window.open(d.url, '_blank', 'noopener')}
    />
  );
}

/** Bytes as the floor would say them. A drawing is usually a few megabytes. */
function fmtSize(bytes: number): string | null {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── standard work ────────────────────────────────────────────────────────────

function StandardWork({ wcName }: { wcName: string | null }) {
  const t = useT();
  const uiLang = useStore((s) => s.language);
  const workcentreId = useStore((s) => s.workcentreId);
  const noteOpened = useStore((s) => s.noteIswOpened);
  const [openId, setOpenId] = useState<string | null>(null);
  // The station's own cards first, and the rest of its department one tap in:
  // the job done here is what the operator came for, and the department is
  // where they look when this station's card is not the one they need.
  const [scope, setScope] = useState<'station' | 'department'>('station');

  const register = useIswRegister(true);
  // The station the app is set to, not the pod's current step: a pod opened
  // from the finder may be sitting anywhere on its route, and the card wanted
  // is the one for the job done here. Its department comes from the work
  // centre list the station picker already holds.
  const workcentres = useWorkcentres();
  const station = workcentres.data?.find((w) => w.id === workcentreId) ?? null;
  const departmentId = station?.departmentId ?? null;
  const departmentName = station?.departmentName ? leafName(station.departmentName) : null;

  const rows = register.data?.rows;
  const here = useMemo(() => iswsForStation(rows ?? [], workcentreId), [rows, workcentreId]);
  const inDepartment = useMemo(() => iswsForDepartment(rows ?? [], departmentId), [rows, departmentId]);
  // Offered only when it would show something this list does not.
  const moreInDepartment = inDepartment.some((r) => !here.includes(r));

  const open = (id: string) => {
    noteOpened(id);
    setOpenId(id);
  };

  const showingDepartment = scope === 'department' && moreInDepartment;

  return (
    <section aria-label={t.iswForYouT}>
      <SectionHead label={t.iswForYouT} note={showingDepartment ? departmentName : wcName} />

      {showingDepartment && (
        <BackButton label={`${t.backToT} ${wcName ?? t.station}`} onClick={() => setScope('station')} />
      )}

      {register.isPending ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={50} radius={12} />
          <Skeleton height={50} radius={12} />
        </div>
      ) : register.isError ? (
        <Banner tone="danger" action={{ label: t.retry, onClick: () => void register.refetch() }}>
          {t.iswFailed}
        </Banner>
      ) : workcentreId == null ? (
        <Muted>{t.iswNoStation}</Muted>
      ) : showingDepartment ? (
        // Each card says which stations it is used at: in a department list,
        // that is the difference between this bay's card and the next one's.
        inDepartment.map((r) => (
          <IswRow
            key={r.id}
            row={r}
            meta={(r.workcenters ?? []).map((w) => w.name).join(' · ')}
            onOpen={() => open(r.id)}
          />
        ))
      ) : (
        <>
          {here.length === 0 && <Muted>{t.iswNoneForStation}</Muted>}
          {here.map((r) => (
            <IswRow key={r.id} row={r} onOpen={() => open(r.id)} />
          ))}
          {moreInDepartment && (
            <Row
              icon="folder"
              tone="sub"
              title={t.iswAllInDept}
              meta={[departmentName, `${inDepartment.length} ${t.iswCardsCount}`].filter(Boolean).join(' · ')}
              onClick={() => setScope('department')}
            />
          )}
        </>
      )}

      {/* Portalled out of the sheet: its panel is centred with a transform,
          which would otherwise be the containing block for the viewer's fixed
          position. The app's root rather than the body, because the palette
          lives there as CSS variables. */}
      {openId &&
        createPortal(
          <CardViewer id={openId} format="mobile" initialLang={LANG_FOR_UI[uiLang] ?? 'en'} onClose={() => setOpenId(null)} />,
          document.querySelector('.ipsf') ?? document.body,
        )}
    </section>
  );
}

function IswRow({ row, meta, onOpen }: { row: IswRegisterRow; meta?: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="press-row"
      onClick={onOpen}
      style={{ ...ROW, borderTop: '1px solid var(--line)' }}
    >
      <IconTile icon="file" tone="accent" />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            font: "700 10.5px 'Space Grotesk',sans-serif",
            color: 'var(--sub)',
            letterSpacing: '.05em',
          }}
        >
          {row.id}
        </span>
        <span style={{ display: 'block', font: '600 13px Manrope,sans-serif', color: 'var(--ink)', textWrap: 'pretty' }}>
          {row.title}
        </span>
        {meta && (
          <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 1 }}>
            {meta}
          </span>
        )}
      </span>
      <StatusPill status={row.status} />
      <span style={{ color: 'var(--sub)', display: 'flex', flex: 'none' }} aria-hidden>
        <Icon name="chevron" size={14} />
      </span>
    </button>
  );
}

// ── shared pieces ────────────────────────────────────────────────────────────

const ROW = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  minHeight: 56,
  padding: '10px 4px',
  background: 'none',
  textAlign: 'left',
} as const;

function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="press"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        minHeight: 36,
        padding: '0 12px',
        marginBottom: 6,
        borderRadius: 10,
        background: 'var(--surface2)',
        color: 'var(--sub)',
        font: '600 11.5px Manrope,sans-serif',
        maxWidth: '100%',
      }}
    >
      <Icon name="back" size={13} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

function SectionHead({ label, note }: { label: string; note: string | null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6, minWidth: 0 }}>
      <span className="sectionLabel" style={{ marginBottom: 0, flex: 'none' }}>
        {label}
      </span>
      {note && (
        <span
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'right',
            font: '600 11px Manrope,sans-serif',
            color: 'var(--sub)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {note}
        </span>
      )}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <p style={{ font: '500 12.5px/1.5 Manrope,sans-serif', color: 'var(--sub)', margin: '4px 0 0' }}>{children}</p>;
}

type Tone = 'accent' | 'danger' | 'sub';

function IconTile({ icon, tone }: { icon: 'file' | 'folder'; tone: Tone }) {
  return (
    <span
      style={{
        width: 38,
        height: 38,
        flex: 'none',
        borderRadius: 11,
        background: 'var(--surface2)',
        display: 'grid',
        placeItems: 'center',
        color: `var(--${tone})`,
      }}
    >
      <Icon name={icon} size={18} />
    </span>
  );
}

function Row({
  icon,
  tone,
  title,
  meta,
  chip,
  trailing = 'chevron',
  disabled = false,
  onClick,
}: {
  icon: 'file' | 'folder';
  tone: Tone;
  title: string;
  meta?: string;
  chip?: { label: string; background: string } | undefined;
  trailing?: 'chevron' | 'external';
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="press-row"
      onClick={onClick}
      disabled={disabled}
      style={{ ...ROW, borderTop: '1px solid var(--line)', opacity: disabled ? 0.55 : 1 }}
    >
      <IconTile icon={icon} tone={tone} />
      <span style={{ flex: 1, minWidth: 0 }}>
        {/* A SharePoint file name is one long unbroken token
            ("4529-AB-R-703-DJ-1.pdf"); it must wrap rather than push the
            sheet sideways. */}
        <span
          style={{ display: 'block', font: '600 13px Manrope,sans-serif', color: 'var(--ink)', overflowWrap: 'anywhere' }}
        >
          {title}
        </span>
        {meta && (
          <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 1 }}>
            {meta}
          </span>
        )}
      </span>
      {chip && (
        <span
          style={{
            padding: '3px 8px',
            borderRadius: 6,
            font: '700 9.5px Manrope,sans-serif',
            letterSpacing: '.03em',
            textTransform: 'uppercase',
            background: chip.background,
            color: '#fff',
            flex: 'none',
          }}
        >
          {chip.label}
        </span>
      )}
      <span style={{ color: 'var(--sub)', display: 'flex', flex: 'none' }} aria-hidden>
        <Icon name={trailing} size={trailing === 'external' ? 15 : 14} />
      </span>
    </button>
  );
}
